import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Activity, Lock, ShieldCheck, Trash2 } from 'lucide-react';
import { useAuditStore } from '../../stores/useAuditStore';
import { loadCredentials, deleteCredentials, claimShopifyInstall, checkLinkedInTokenHealth } from '../../api/client';
import { PROVIDER_TO_PLATFORM } from '../../data/platforms';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import PlatformGrid from './PlatformGrid';
import FileUploadZone from './FileUploadZone';
import TikTokAccountSelector from './TikTokAccountSelector';
import ReportHistory from './ReportHistory';
import GAPropertySelector from './GAPropertySelector';
import GoogleAdsAccountSelector from './GoogleAdsAccountSelector';
import MetaAccountSelector from './MetaAccountSelector';
import { clearPageGrantDraft, readMetaRedirect, type MetaGrantReturn } from './metaPages';
import { clearInstagramGrantDraft } from './metaInstagram';
import OnboardingFooter from '../onboarding/OnboardingFooter';
import LinkedInAccountSelector from './LinkedInAccountSelector';
import { useStageGuard } from '../../hooks/useStageGuard';


/** Reassurance about what connecting actually grants. Shown up front because
 *  this is the screen where the user hands over access. */
const ASSURANCES = [
  { Icon: Lock, label: 'Encrypted end to end' },
  { Icon: Activity, label: 'Read-only access' },
  { Icon: ShieldCheck, label: 'GDPR compliant' },
  { Icon: Trash2, label: 'Delete anytime' },
];

export default function ConnectView() {
  const navigate = useNavigate();
  // Forward-redirect into /processing, /generate-report, or /report if the
  // user has already launched (or finished) an audit.
  useStageGuard('/connect');
  const [searchParams, setSearchParams] = useSearchParams();
  const { connectedSources, uploadedFiles, connectPlatform, disconnectPlatform } = useAuditStore();
  const [showTikTokSelector, setShowTikTokSelector] = useState(false);
  const oauthHandled = useRef(false);
  const [gaSession, setGaSession] = useState<string | null>(null);
  const [gaError, setGaError] = useState<string | null>(null);
  const [googleAdsSession, setGoogleAdsSession] = useState<string | null>(null);
  const [googleAdsError, setGoogleAdsError] = useState<string | null>(null);
  const [metaSession, setMetaSession] = useState<string | null>(null);
  const [metaError, setMetaError] = useState<string | null>(null);
  // Outcome of the on-demand Facebook Page grant (VIBE-1916). Read once, then
  // dropped from storage: it describes one round trip, and a leftover would make
  // a later mount report a result the user never asked for. It phrases the
  // message only; `GET /pages` remains the authoritative state.
  const [metaGrantReturn, setMetaGrantReturn] = useState<MetaGrantReturn>({ kind: 'none' });
  const [linkedInSession, setLinkedInSession] = useState<string | null>(null);
  const [linkedInError, setLinkedInError] = useState<string | null>(null);
  const [auditError, setAuditError] = useState<string | null>(null);

  // The audit was halted because no usable data could be extracted from any
  // connected source (ProcessingView / post-login route sent the user back here
  // with ?audit_error=no_data). Surface a banner, then strip the param.
  useEffect(() => {
    if (searchParams.get('audit_error') === 'no_data') {
      setAuditError(
        "We couldn't extract any data from your connected sources. " +
        'Check that they’re still connected (re-authorize if needed), then continue.'
      );
      setSearchParams((prev) => { prev.delete('audit_error'); return prev; }, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle TikTok OAuth callback results (e.g., ?tiktok=success or ?tiktok=select)
  useEffect(() => {
    if (oauthHandled.current) return;
    const url = new URL(window.location.href);
    const tiktokResult = url.searchParams.get('tiktok');
    if (!tiktokResult) return;
    oauthHandled.current = true;
    url.searchParams.delete('tiktok');
    url.searchParams.delete('reason');
    window.history.replaceState(null, '', url.pathname + url.search);
    if (tiktokResult === 'success') {
      connectPlatform('tiktok-ads');
    } else if (tiktokResult === 'select') {
      setShowTikTokSelector(true);
    } else if (tiktokResult === 'error') {
      console.error('TikTok OAuth error:', url.searchParams.get('reason') || 'Unknown');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Shopify results. A Shopify-initiated install (App Store req 2.3.1) lands
  // here with ?shopify=claim&shop= — the store was parked backend-side, so we claim
  // it onto the signed-in account. success/error remain for other/legacy paths.
  useEffect(() => {
    const url = new URL(window.location.href);
    const shopifyResult = url.searchParams.get('shopify');
    if (!shopifyResult) return;
    const reason = url.searchParams.get('reason') || 'Unknown';
    const shop = url.searchParams.get('shop');
    url.searchParams.delete('shopify');
    url.searchParams.delete('reason');
    url.searchParams.delete('shop');
    window.history.replaceState(null, '', url.pathname + url.search);
    if (shopifyResult === 'claim' && shop) {
      claimShopifyInstall(shop)
        .then((r) => {
          if (r.status === 'connected') connectPlatform('shopify');
          else console.error('Shopify claim failed:', r.error);
        })
        .catch((e) => console.error('Shopify claim failed:', e));
    } else if (shopifyResult === 'success') {
      connectPlatform('shopify');
    } else if (shopifyResult === 'error') {
      console.error('Shopify OAuth error:', reason);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let session = searchParams.get('ga_session');
    let error = searchParams.get('ga_error');
    if (!session) {
      try { session = sessionStorage.getItem('pending_ga_session'); } catch { /* ignore */ }
    }
    if (!error) {
      try { error = sessionStorage.getItem('pending_ga_error'); } catch { /* ignore */ }
    }
    if (session) {
      setGaSession(session);
      try { sessionStorage.setItem('pending_ga_session', session); } catch { /* ignore */ }
      if (searchParams.get('ga_session')) setSearchParams({}, { replace: true });
    }
    if (error) {
      setGaError(`Google Analytics connection failed: ${error}`);
      try { sessionStorage.setItem('pending_ga_error', error); } catch { /* ignore */ }
      if (searchParams.get('ga_error')) setSearchParams({}, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearGASession = () => {
    setGaSession(null);
    try { sessionStorage.removeItem('pending_ga_session'); } catch { /* ignore */ }
  };

  useEffect(() => {
    let session = searchParams.get('google_ads_session');
    let error = searchParams.get('google_ads_error');
    if (!session) {
      try { session = sessionStorage.getItem('pending_google_ads_session'); } catch { /* ignore */ }
    }
    if (!error) {
      try { error = sessionStorage.getItem('pending_google_ads_error'); } catch { /* ignore */ }
    }
    if (session) {
      setGoogleAdsSession(session);
      try { sessionStorage.setItem('pending_google_ads_session', session); } catch { /* ignore */ }
      if (searchParams.get('google_ads_session')) setSearchParams({}, { replace: true });
    }
    if (error) {
      setGoogleAdsError(`Google Ads connection failed: ${error}`);
      try { sessionStorage.setItem('pending_google_ads_error', error); } catch { /* ignore */ }
      if (searchParams.get('google_ads_error')) setSearchParams({}, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearGoogleAdsSession = () => {
    setGoogleAdsSession(null);
    try { sessionStorage.removeItem('pending_google_ads_session'); } catch { /* ignore */ }
  };

  useEffect(() => {
    let session = searchParams.get('meta_session');
    let error = searchParams.get('meta_error');
    if (!session) {
      try { session = sessionStorage.getItem('pending_meta_session'); } catch { /* ignore */ }
    }
    if (!error) {
      try { error = sessionStorage.getItem('pending_meta_error'); } catch { /* ignore */ }
    }
    let granted = searchParams.get('meta_pages_granted');
    if (!granted) {
      try { granted = sessionStorage.getItem('pending_meta_pages_granted'); } catch { /* ignore */ }
    }
    // One decision, taken in `readMetaRedirect`: which of these params means the
    // connection broke and which means a change was declined. `ads_scope_revoked`
    // arrives WITH a live session — the server kept the working token rather than
    // writing the degraded one — so rendering the red "Meta connection failed"
    // banner for it would report a break over a connection that still works, and
    // do it behind a wizard that is still open and still usable.
    const decided = readMetaRedirect({ session, error, pagesGranted: granted });
    if (decided.session) {
      setMetaSession(decided.session);
      try { sessionStorage.setItem('pending_meta_session', decided.session); } catch { /* ignore */ }
    }
    if (decided.fatalError) {
      setMetaError(`Meta connection failed: ${decided.fatalError}`);
      try { sessionStorage.setItem('pending_meta_error', decided.fatalError); } catch { /* ignore */ }
    } else {
      // A non-fatal reason is consumed here and must not survive the mount, or a
      // later visit re-reports a round trip that is long over.
      try { sessionStorage.removeItem('pending_meta_error'); } catch { /* ignore */ }
    }
    if (decided.grantReturn.kind !== 'none') setMetaGrantReturn(decided.grantReturn);
    try { sessionStorage.removeItem('pending_meta_pages_granted'); } catch { /* ignore */ }
    if (
      searchParams.get('meta_session') ||
      searchParams.get('meta_error') ||
      searchParams.get('meta_pages_granted')
    ) {
      setSearchParams({}, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearMetaSession = () => {
    setMetaSession(null);
    setMetaGrantReturn({ kind: 'none' });
    try { sessionStorage.removeItem('pending_meta_session'); } catch { /* ignore */ }
    try { sessionStorage.removeItem('pending_meta_pages_granted'); } catch { /* ignore */ }
    clearPageGrantDraft();
    // The Instagram grant parks its own draft (VIBE-1990). It belongs to this
    // session, so it ends with it — exactly like the Page draft above.
    clearInstagramGrantDraft();
  };

  useEffect(() => {
    let session = searchParams.get('linkedin_session');
    let error = searchParams.get('linkedin_error');
    if (!session) {
      try { session = sessionStorage.getItem('pending_linkedin_session'); } catch { /* ignore */ }
    }
    if (!error) {
      try { error = sessionStorage.getItem('pending_linkedin_error'); } catch { /* ignore */ }
    }
    if (session) {
      setLinkedInSession(session);
      try { sessionStorage.setItem('pending_linkedin_session', session); } catch { /* ignore */ }
      if (searchParams.get('linkedin_session')) setSearchParams({}, { replace: true });
    }
    if (error) {
      setLinkedInError(
        error === 'access_denied'
          ? 'LinkedIn connection cancelled.'
          : `LinkedIn connection failed: ${error}`,
      );
      try { sessionStorage.setItem('pending_linkedin_error', error); } catch { /* ignore */ }
      if (searchParams.get('linkedin_error')) setSearchParams({}, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearLinkedInSession = () => {
    setLinkedInSession(null);
    try { sessionStorage.removeItem('pending_linkedin_session'); } catch { /* ignore */ }
  };

  useEffect(() => {
    loadCredentials()
      .then((creds) => {
        for (const [provider, keys] of Object.entries(creds)) {
          if (keys && keys.length > 0) {
            const platformId = PROVIDER_TO_PLATFORM[provider];
            if (platformId) {
              connectPlatform(platformId);
            }
          }
        }
        // LinkedIn access tokens live ~60 days and the app gets no refresh
        // token unless it is an approved MDP partner, so a stored credential
        // can be long dead. Probe once here rather than letting the audit
        // discover it mid-run.
        if (creds.linkedin?.length) {
          checkLinkedInTokenHealth()
            .then(({ connected, valid }) => {
              if (connected && !valid) {
                disconnectPlatform('linkedin-ads');
                setLinkedInError('LinkedIn access expired. Please reconnect.');
              }
            })
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [connectPlatform, disconnectPlatform]);

  const handleGAConnected = (_count: number) => {
    connectPlatform('ga');
    clearGASession();
  };

  const handleGoogleAdsConnected = (_count: number) => {
    connectPlatform('google-ads');
    clearGoogleAdsSession();
  };

  const handleMetaConnected = (_count: number) => {
    connectPlatform('meta-ads');
    clearMetaSession();
  };

  const handleLinkedInConnected = () => {
    connectPlatform('linkedin-ads');
    setLinkedInError(null);
    try { sessionStorage.removeItem('pending_linkedin_error'); } catch { /* ignore */ }
    clearLinkedInSession();
  };

  const connectedCount = connectedSources.filter((s) => s.status === 'connected').length;
  const hasAnySources = connectedCount > 0 || uploadedFiles.length > 0;

  const connectionSummary = (() => {
    const parts: string[] = [];
    if (connectedCount) parts.push(`${connectedCount} connected`);
    if (uploadedFiles.length) {
      parts.push(`${uploadedFiles.length} file${uploadedFiles.length === 1 ? '' : 's'} uploaded`);
    }
    return parts.length ? parts.join(' · ') : 'No platforms connected yet';
  })();

  return (
    <>
      {gaSession && (
        <GAPropertySelector
          sessionId={gaSession}
          onSaved={handleGAConnected}
          onClose={clearGASession}
        />
      )}
      {googleAdsSession && (
        <GoogleAdsAccountSelector
          sessionId={googleAdsSession}
          onSaved={handleGoogleAdsConnected}
          onClose={clearGoogleAdsSession}
        />
      )}
      {metaSession && (
        <MetaAccountSelector
          // Remount on a new session: the wizard holds a draft, a baseline and a
          // page selection keyed to ONE session, and carrying any of them into a
          // different connect attempt is a data-loss bug. One word makes it
          // impossible by construction rather than by review.
          key={metaSession}
          sessionId={metaSession}
          grantReturn={metaGrantReturn}
          onSaved={handleMetaConnected}
          onClose={clearMetaSession}
        />
      )}
      {linkedInSession && (
        <LinkedInAccountSelector
          sessionId={linkedInSession}
          onSaved={handleLinkedInConnected}
          onClose={clearLinkedInSession}
        />
      )}
      <div className="mx-auto max-w-[1180px] px-4 pb-24 pt-8 sm:px-8">
      <Tabs defaultValue="platforms">
        <div className="mb-6 flex items-center justify-between">
          <TabsList variant="line">
            <TabsTrigger value="platforms">Platforms</TabsTrigger>
            <TabsTrigger value="reports">Report History</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="platforms">
          <div className="mb-1">
            <h1 className="text-xl font-bold tracking-tight text-foreground">Connect Your Platforms</h1>
            <p className="text-sm text-muted-foreground">
              Connect a platform for live data, or upload exports — CSV, XLSX, PDF, PNG and more.
              No cleaning needed.
            </p>
          </div>

          <div className="mb-5 mt-4 flex flex-wrap gap-2">
            {ASSURANCES.map(({ Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 rounded-full border bg-card px-3.5 py-1.5 text-[12.5px] font-semibold text-foreground shadow-xs"
              >
                <Icon className="size-3.5 shrink-0 text-success" />
                {label}
              </span>
            ))}
          </div>

          <PlatformGrid />

          {/* Uploads come after the grid: connecting is the preferred path, and
              the dropzone is the fallback for platforms we can't reach live. */}
          <div className="mt-4">
            <FileUploadZone />
          </div>
        </TabsContent>

        <TabsContent value="reports">
          <ReportHistory />
        </TabsContent>
      </Tabs>

      {auditError && (
        <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {auditError}
          <button
            type="button"
            onClick={() => setAuditError(null)}
            className="ml-2 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {gaError && (
        <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {gaError}
          <button
            type="button"
            onClick={() => setGaError(null)}
            className="ml-2 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {googleAdsError && (
        <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {googleAdsError}
          <button
            type="button"
            onClick={() => {
              setGoogleAdsError(null);
              try { sessionStorage.removeItem('pending_google_ads_error'); } catch { /* ignore */ }
            }}
            className="ml-2 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {metaError && (
        <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {metaError}
          <button
            type="button"
            onClick={() => {
              setMetaError(null);
              try { sessionStorage.removeItem('pending_meta_error'); } catch { /* ignore */ }
            }}
            className="ml-2 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {linkedInError && (
        <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {linkedInError}
          <button
            type="button"
            onClick={() => {
              setLinkedInError(null);
              try { sessionStorage.removeItem('pending_linkedin_error'); } catch { /* ignore */ }
            }}
            className="ml-2 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* No Back button: this is the first required step in the audit flow. */}
      <OnboardingFooter
        step={2}
        note={connectionSummary}
        continueDisabled={!hasAnySources}
        onContinue={() => navigate('/collect')}
      />

      {/* TikTok Account Selector Modal */}
      {showTikTokSelector && (
        <TikTokAccountSelector
          onClose={async () => {
            setShowTikTokSelector(false);
            // User cancelled without selecting — delete credentials and disconnect
            try {
              await deleteCredentials('tiktok');
            } catch { /* best effort */ }
            disconnectPlatform('tiktok-ads');
          }}
          onSaved={() => {
            setShowTikTokSelector(false);
            connectPlatform('tiktok-ads');
          }}
        />
      )}
    </div>
    </>
  );
}
